/**
 * Canonical world axes — single source of truth (Phase A5).
 *
 * THE CONVENTION: the shared document/world space is RIGHT-HANDED, Z-UP,
 * base unit meters, for every workbench (BIM / 2D / 3D).
 * This matches the BIM kernel, the NURBS model and the 2D/DXF native
 * format. CAD drafting tools were migrated from their original Y-up/XZ
 * ground convention to this one in A5; no new code may assume Y is up.
 *
 * Rules for all scene-writing code (see docs/architecture/
 * single-app-integration-plan.md, Golden Rule 6):
 * - The ground / construction plane is XY: normal = UP.
 * - "Height / elevation / extrude direction" is +Z.
 * - Snap to ground grid on (x, y); z is the level coordinate.
 * - Ortho constraints compare dx/dy.
 * - Cameras look down at plan from +Z with up = +Y (three.js requires an
 *   up vector non-parallel to the view direction).
 *
 * Import these instead of hand-building planes/vectors so a future change
 * of convention stays a one-line operation.
 */

import * as THREE from 'three';

/** World up axis (canonical): +Z. */
export const UP_AXIS: readonly [number, number, number] = [0, 0, 1];

/** Ground-plane normal: +Z. */
export const UP = new THREE.Vector3(0, 0, 1);

/**
 * The shared ground / construction plane: XY at Z = 0.
 * Read-only usage only (intersectPlane does not mutate); never modify.
 */
export const GROUND_PLANE = new THREE.Plane(UP, 0);

/** Planar (XY) axes of the canonical frame, for grid/workplane math. */
export const X_AXIS = new THREE.Vector3(1, 0, 0);
export const Y_AXIS = new THREE.Vector3(0, 1, 0);

/**
 * Snap a point to a square ground grid on the XY plane, pinning the level
 * coordinate (z) unchanged. `elevation` optionally overrides z.
 */
export function snapToGroundGrid(
    point: THREE.Vector3,
    gridSize: number,
    elevation?: number
): THREE.Vector3 {
    return new THREE.Vector3(
        Math.round(point.x / gridSize) * gridSize,
        Math.round(point.y / gridSize) * gridSize,
        elevation ?? point.z
    );
}

/**
 * Ortho (horizontal/vertical) constraint in the XY ground plane: keeps the
 * dominant axis delta and zeroes the other, preserving z (level).
 */
export function applyGroundOrtho(
    from: THREE.Vector3,
    to: THREE.Vector3,
    enabled: boolean
): THREE.Vector3 {
    if (!enabled) return to.clone();
    const dx = Math.abs(to.x - from.x);
    const dy = Math.abs(to.y - from.y);
    return dx > dy
        ? new THREE.Vector3(to.x, from.y, to.z)
        : new THREE.Vector3(from.x, to.y, to.z);
}
